import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Modal } from '@mui/material';
import { omit } from 'lodash';
import { ProjectTypeEnum } from '~/api/types';
import { CreateProjectBody } from '~/api/v2/endpoints/project';
import {
  useCreateProjectMutation,
  useDeactivateProjectMutation,
  useEditProjectMutation,
  useGetProjectQuery,
  useGetProjectWorkbenchesQuery,
} from '~/api/v2/hooks';
import { ModalHeader, Tooltip } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { ModalTypesEnum, closeModalById, openModal } from '~/redux/slices/modalSlice';
import { projectSchema } from '~/validation';
import { ProjectForm } from '.';
import { removeButtonStyles } from '../../DatasetDetailsPage/components/DatasetModal/styles';
import { loadingContainerStyles } from './styles';

const ProjectEditPanel = ({ id }: { id: string }) => {
  const projectId = +useParams().projectid!;
  const dispatch = useAppDispatch();
  const { data: project } = useGetProjectQuery({ projectId }, { skip: !projectId });
  const [createProject, { isLoading: isCreatingProject }] = useCreateProjectMutation({});
  const [editProject, { isLoading: isEditingProject }] = useEditProjectMutation({});
  const [, { isSuccess: isProjectDeactivated }] = useDeactivateProjectMutation({
    fixedCacheKey: `deactivate-project-${project?.id}`,
  });
  const isLoading = isCreatingProject || isEditingProject;
  const { data: workbenches } = useGetProjectWorkbenchesQuery(
    { projectId, showTerminated: false },
    { skip: !projectId },
  );

  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(projectSchema),
    defaultValues: {
      name: project?.name,
      type: project?.type || ProjectTypeEnum.CONSUMER,
      description: project?.description,
      operating_unit_id: project?.operating_unit_id,
      status: project?.status,
      wbs_code_id: project?.wbs_code_id,
      cost_center_id: project?.cost_center_id,
      dataset_ids: projectId && project?.datasets?.length ? project?.datasets?.map(({ id }) => id) : [],
      destination_dataset_ids:
        projectId && project?.destination_datasets?.length ? project?.destination_datasets?.map(({ id }) => id) : [],
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  const isNewProject = !project;
  const isDeactivateProjectDisabled = !!workbenches?.data?.length;

  const intraHandleClose = () => {
    dispatch(closeModalById(id));
    reset();
  };

  const handleCreateNewProject = async (data: CreateProjectBody) => {
    await createProject({
      ...omit(
        data,
        [data?.type === ProjectTypeEnum.CONSUMER && 'destination_dataset_ids', 'status'].filter(Boolean) as string[],
      ),
    });
    intraHandleClose();
  };

  const handleEditProject = async (data: CreateProjectBody) => {
    await editProject({ projectId: projectId!, type: project?.type, ...omit(data, 'status') });
    intraHandleClose();
  };

  useEffect(() => {
    if (isProjectDeactivated) {
      dispatch(closeModalById(id));
    }
  }, [isProjectDeactivated]);

  return (
    <Modal open onClose={isLoading ? () => {} : intraHandleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader
          onClose={isLoading ? () => {} : intraHandleClose}
          title={[
            isNewProject ? 'Create a New Project' : `Edit Project - "${project.name}"`,
            `Please enter the following details to ${isNewProject ? 'create a new' : 'edit a'}  project.`,
          ]}
        />
        {isLoading ? (
          <Box sx={loadingContainerStyles}>
            <LoadingSpinner />
          </Box>
        ) : (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(isNewProject ? handleCreateNewProject : handleEditProject)}>
              <ProjectForm />
              <Box sx={modalActionBlockStyles}>
                <CustomButton buttonType="borderless" onClick={intraHandleClose} label="Close" />
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  {!isNewProject && (
                    <Tooltip
                      title={
                        isDeactivateProjectDisabled
                          ? 'Cannot close a project with an active workbench.'
                          : 'Mark the project as inactive. This action cannot be undone.'
                      }
                    >
                      <CustomButton
                        disabled={isDeactivateProjectDisabled}
                        label="Deactivate Project"
                        sx={removeButtonStyles}
                        onClick={() => {
                          dispatch(openModal({ type: ModalTypesEnum.DEACTIVATE_PROJECT }));
                        }}
                      />
                    </Tooltip>
                  )}
                  <CustomButton type="submit" label="Submit" disabled={!(isValid && isDirty)} />
                </Box>
              </Box>
            </form>
          </FormProvider>
        )}
      </Box>
    </Modal>
  );
};

export default ProjectEditPanel;
